import type { FC, MouseEvent } from 'react';
import React, { Fragment, memo, useContext, useState, useCallback, useRef, useEffect } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import find from 'lodash/find';
import uuid from 'uuid/v4';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import memoizeOne from 'memoize-one';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/experimental/open-layer-observer';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import { useConsumptionEvents } from '@confluence/unified-analytics/entry-points/useConsumptionEvents.ts';
import {
	PAGE_TREE_EXPERIENCE,
	PAGE_TREE_DND_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	checkSSRPartialSuccess,
	SSR_PARTIAL_COMPONENT,
} from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';
import {
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_navigationView,
} from '@confluence/performance/entry-points/constants.ts';
import { perfMarkEnd, getMark } from '@confluence/performance/entry-points/performance.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { UNDO_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { CREATE_CONTENT } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { scrollElementToViewCenter } from '@confluence/dom-helpers';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { generatePathWithFeatureCodesQueryParam } from '@confluence/feature-codes/entry-points/featureCodes.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';
import { expVal } from '@confluence/feature-experiments';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { usePageState } from '@confluence/page-context';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import {
	useOutboundPageTreeUpdatePublisher,
	OutboundPageTreeMessage,
} from '@confluence/page-tree-refresh-state-container/entry-points/pageTreeOutboundBus.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import type { HoverPageCardProps } from '@confluence/page-card/entry-points/useHoverPageCard.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import type {
	ItemId,
	TreeDestinationPosition,
	TreeSourcePosition,
} from '@confluence/tree/entry-points/tree-types.ts';
import { getAutomatedMeetingNotesPageProperties } from '@confluence/automated-meeting-notes/entry-points/getAutomatedMeetingNotesPageProperties.ts';

import type { ContentTreeItem } from './data-transformers.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
import { PAGE_TREE_STATUSES } from './pageTreeStatuses.ts';
import type { PageTreeCoordinatorProps } from './PageTreeCoordinator.tsx';
import { PageTreeCoordinator } from './PageTreeCoordinator.tsx';
import { renderPageTreeItem } from './renderPageTreeItem.tsx';
import type { MovePageParams } from './useMovePageHandler.ts';
import { useMovePageHandler } from './useMovePageHandler.ts';
import {
	dragDropEndTrackEvent,
	dragStartUIEvent,
	dragEndUIEvent,
	pageTreeUIAnalytics,
	undoClickUIEvent,
	undoShortcutUIEvent,
	pageTreePaginationEvent,
} from './analytics.ts';
import { PageMoveHistory } from './PageMoveHistory.ts';
import { PAGE_TREE_METRIC } from './perf.config.js';
import { getI18nMessageForKnownError } from './movePublishedPageErrorHelpers.tsx';
import { DragPreview } from './DragPreview.tsx';
import { useIsDragging } from './use-is-dragging.ts';
import type { loadingErrorLocation } from './usePageTreeState.ts';
import { i18n } from './errorFlagMessages.ts';

const noop = () => {};

export type PageTreeProps = {
	isInDrawer?: boolean;
	onDragDropSuccess?(pageId: string): void;
	onItemClick?(): void;
	isPeekingFromBlogs?: boolean;
	isSuperAdmin?: boolean;
	setIsPageTreeLoading?: (isLoading: boolean) => void;
	setPageTreeError?: (error?: Error) => void;
	isQuickActionsEnabled?: boolean;
	isInlineRenameEnabled?: boolean;
	shouldRenderAfterIcon?: boolean;
	homepageId?: string;
	setContentTreeSize?: (size: number) => void;
	onContentTreeLoadComplete?: () => void;
} & Partial<HoverPageCardProps>;

type PageTreeInnerProps = {
	pageTreeStateUpdatesContainer: PageTreeStateUpdater;
	flags: FlagsStateContainer;
};

const canMoveInSpace = memoizeOne(
	(space) => {
		return !!find(space?.operations ?? [], {
			targetType: 'page',
			operation: 'create',
		});
	},
	([newSpace], [lastSpace]): boolean => {
		return newSpace?.id === lastSpace?.id;
	},
);

const isMovePagePermissionError = ({ graphQLErrors }: ApolloErrorInterop) => {
	if (!graphQLErrors?.length) {
		return false;
	}

	for (const graphQLError of graphQLErrors) {
		if (
			Array.isArray(graphQLError.path) &&
			graphQLError.path.some((p) => p.includes('movePage')) &&
			getStatusCode(graphQLError) === '403'
		) {
			return true;
		}

		if (graphQLError.message?.includes('PermissionException')) {
			return true;
		}
	}

	return false;
};

const usePageTreeMoveHistory = ({
	spaceKey,
	flags,
	intl,
	createAnalyticsEvent,
}: {
	spaceKey?: string;
	flags: FlagsStateContainer;
	intl: IntlShape;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}) => {
	const previousSpaceKeyRef = useRef(spaceKey);
	const pageMoveHistoryRef = useRef(new PageMoveHistory());

	const undo = useCallback(() => {
		const reverseMovement = pageMoveHistoryRef.current.pop();

		if (reverseMovement) {
			const { undo: undoFn } = reverseMovement;

			if (undoFn) {
				undoFn();
			}
		}
	}, []);

	const onUndoShortcut = useCallback(() => {
		createAnalyticsEvent(undoShortcutUIEvent()).fire();
		undo();
	}, [createAnalyticsEvent, undo]);

	const showUndoFlag = useCallback(
		(pageTitle: string): void => {
			const id = uuid();
			void flags.showSuccessFlag({
				id,
				title: pageTitle
					? intl.formatMessage(i18n.flagUndoTitle, {
							pageTitle,
						})
					: intl.formatMessage(i18n.flagUndoUntitled),
				actions: [
					{
						content: intl.formatMessage(i18n.flagUndoAction),
						onClick: () => {
							createAnalyticsEvent(undoClickUIEvent()).fire();
							undo();
							void flags.hideFlag(id);
						},
					},
				],
				isAutoDismiss: true,
			});

			const historyEntry = pageMoveHistoryRef.current.peek();
			if (historyEntry) {
				historyEntry.flagId = id;
			}
		},
		[flags, intl, createAnalyticsEvent, undo],
	);

	const hideLastUndoFlag = useCallback(() => {
		const lastMovement = pageMoveHistoryRef.current.peek();
		if (lastMovement) {
			void flags.hideFlag(lastMovement.flagId);
		}
	}, [flags]);

	const addUndo = useCallback(({ undo }: { undo(): void }) => {
		pageMoveHistoryRef.current.push({ undo });
	}, []);

	if (spaceKey !== previousSpaceKeyRef.current) {
		previousSpaceKeyRef.current = spaceKey;
		hideLastUndoFlag();
		pageMoveHistoryRef.current.reset();
	}

	return {
		onUndoShortcut,
		showUndoFlag,
		hideLastUndoFlag,
		addUndo,
	};
};

const useScrollToItem = ({
	pageTreeStateUpdatesContainer,
}: {
	pageTreeStateUpdatesContainer: PageTreeStateUpdater;
}) => {
	const scrolledItemIdRef = useRef();

	const scrollToItem = useCallback(
		(item: any, el: any) => {
			if (pageTreeStateUpdatesContainer.state.scrollToPageId === item.id && el) {
				scrollElementToViewCenter(el);
				pageTreeStateUpdatesContainer.clearScrollToPageId();
				return;
			}

			if (
				!pageTreeStateUpdatesContainer.state.editingTitle?.contentId &&
				(item.data.isSelected || item.data.isHighlighted)
			) {
				if (pageTreeStateUpdatesContainer.state.scrollToCurrent && item.data.isSelected && el) {
					scrollElementToViewCenter(el);
					pageTreeStateUpdatesContainer.clearScrollTo();
					return;
				}

				if (scrolledItemIdRef.current !== item.id) {
					// Scroll only once to a given page
					scrollElementToViewCenter(el);
				}
				scrolledItemIdRef.current = item.id;
			}
		},
		[pageTreeStateUpdatesContainer],
	);

	return [scrollToItem];
};

const usePageHighlight = ({
	pageTreeStateUpdatesContainer,
}: {
	pageTreeStateUpdatesContainer: PageTreeStateUpdater;
}) => {
	const highlightedPages = useRef<ItemId[]>([]);

	const onPageHighlighted = useCallback(
		(id: ItemId) => {
			highlightedPages.current.push(id);
			// keep a tally of the successfully highlighted pages and notify
			// parent when they've all completed
			const pagesInTreeHighlighted = pageTreeStateUpdatesContainer.state.highlightPagesIds;

			// Checking length rather than deep equality prevents UI weirdness
			// should there happen to be a mismatch. (In that case, deep equality
			// condition would never pass for duration of this component's life, and
			// we would keep re-highlighting the same pages on every tree render.)
			// @TODO FIXME to use Set instead of array.
			if (highlightedPages.current.length === pagesInTreeHighlighted.length) {
				pageTreeStateUpdatesContainer.clearHighlightedPages();
				highlightedPages.current = [];
			}
		},
		[pageTreeStateUpdatesContainer],
	);

	return [onPageHighlighted];
};

/**
 * This component is what makes the generic `PageTreeController` into a fully functional
 * navigational Page Tree that we all love. It is the one to actually handle things like:
 *  - analytics, performance, and experience tracking events
 *  - backend mutations after drag-n-drop
 *  - orchestrating "undo" for the d-n-d
 *  - showing flags of various kinds (successes/failures). Although triggers for these might originate in other components.
 */
const PageTreeInner = ({
	isInDrawer = false,
	onDragDropSuccess = noop,
	onItemClick,
	isPeekingFromBlogs,
	isSuperAdmin = false,
	pageTreeStateUpdatesContainer,
	flags,
	setIsPageTreeLoading,
	setPageTreeError = noop,
	isHoverPageCardOptedIn = false,
	closeAllHoverPageCards = noop,
	isQuickActionsEnabled = false,
	isInlineRenameEnabled = false,
	onEnterHoverTarget = noop,
	onLeaveHoverTarget = noop,
	onBlurHoverTarget = noop,
	shouldRenderAfterIcon,
	homepageId,
	setContentTreeSize = noop,
	onContentTreeLoadComplete = noop,
}: PageTreeProps & PageTreeInnerProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const { push } = useRouteActions();
	const [{ spaceKey, contentId, contentIdLoading, routeName }] = usePageState();
	const typeSafeSpaceKey = spaceKey || '';

	// Many analytics callbacks are sending the current viewing page. The problem is these callbacks are passed to every page tree item.
	// If we use contentId directly the callback functions are going to be regenerated and making all page tree items re-render.
	const contentIdForAnalyticsRef = useRef<string | null | undefined>(contentId);

	const { loadEditor } = useEditPageLoadingActions();

	const [openQuickActionsId, setOpenQuickActionsId] = useState<string | null>(null);

	const [focusedQuickActionsId, setFocusedQuickActionsId] = useState<string | null>(null);

	const { setIsDraggingItem, getIsDraggingItem } = useIsDragging();

	const isOnEditRoute = useIsEditorPage();
	const isOnEditRouteRef = useRef<boolean>();

	useEffect(() => {
		isOnEditRouteRef.current = isOnEditRoute;
		contentIdForAnalyticsRef.current = contentId;
	}, [isOnEditRoute, contentId]);

	const renderResourcedEmoji = useRef<boolean>(false);

	const { onUndoShortcut, showUndoFlag, hideLastUndoFlag, addUndo } = usePageTreeMoveHistory({
		flags,
		intl,
		createAnalyticsEvent,
		spaceKey,
	});

	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'page-tree-page-tree-item',
	});

	const { sendInitiatedEvent } = useConsumptionEvents();

	const visitPage = useCallback(
		(event: any, page: any, spaceId?: string | null, getContentTreeSize?: () => number) => {
			const contentTreeSize = getContentTreeSize?.();
			closeAllHoverPageCards?.();
			createAnalyticsEvent(
				pageTreeUIAnalytics(
					'clicked',
					spaceId, //spaceId was already passing Undefined
					isInDrawer,
					page.data?.status,
					contentIdForAnalyticsRef.current,
					page.data?.type,
					START_TOUCH,
					isOnEditRouteRef.current,
					contentTreeSize,
					typeSafeSpaceKey,
				),
			).fire();

			// metaKey = ⌘ in macOS or middle scroll mouse button
			// Use default browser behaviour when any of these key was pressed
			if (event.metaKey || event.shiftKey || event.altKey || event.ctrlKey || event.button === 1) {
				event.currentTarget.href = generatePathWithFeatureCodesQueryParam(
					event.currentTarget.href,
					[FeatureCode.PAGE_TREE],
				);
				return; // let the link's href handle the navigation
			}

			sendInitiatedEvent({
				contentId: page.id,
				spaceKey: typeSafeSpaceKey,
				source: 'PageTree',
				utmSource: 'PAGETREE',
			});

			const url = `${CONTEXT_PATH}${page.data?.webui}`;
			const bypassURL = getURLBypassingResumeDraftAction({
				url,
				spaceKey: typeSafeSpaceKey,
				contentType: page?.data?.type,
				editorVersion: page?.data?.editorVersion,
				contentId: page.id,
			});

			const isDraft = page.data?.status === 'DRAFT';
			if (isDraft) {
				event.preventDefault();
				event.stopPropagation();
				loadEditor({
					contentId: page.id,
					spaceKey: typeSafeSpaceKey,
					redirectUrl: bypassURL,

					isFabricSupported: page?.data?.editorVersion === 'v2',
					isEmbeddedContent: false,
				});
			} else {
				event.preventDefault();
				push(generatePathWithFeatureCodesQueryParam(url, [FeatureCode.PAGE_TREE]));
			}
			if (onItemClick) {
				onItemClick();
			}
		},
		[
			push,
			onItemClick,
			typeSafeSpaceKey,
			closeAllHoverPageCards,
			createAnalyticsEvent,
			isInDrawer,
			loadEditor,
			isOnEditRouteRef,
			sendInitiatedEvent,
		],
	);

	const handleClick = useCallback(
		(
			event: MouseEvent,
			page: ContentTreeItem,
			onExpand: ((itemId: string) => void) | undefined,
			onCollapse: ((itemId: string) => void) | undefined,
			...args
		) => {
			if (page.data.type === 'folder') {
				if (expVal('confluence_folder_view', 'isEnabled', false)) {
					// Toggle tree expand/collapse and navigate to folder view
					if (page.isExpanded) {
						onCollapse?.(page.id);
						traceUFOPress('page-tree-item-collapse', event.timeStamp);
					} else {
						onExpand?.(page.id);
						traceUFOPress('page-tree-item-expand', event.timeStamp);
					}
				} else {
					event.preventDefault();
					page.isExpanded ? onCollapse?.(page.id) : onExpand?.(page.id);
					if (page.isExpanded) {
						traceUFOPress('page-tree-item-collapse', event.timeStamp);
					} else {
						traceUFOPress('page-tree-item-expand', event.timeStamp);
					}
					return;
				}
			}

			// Check for storage enforcement before sending users through to draft pages
			const isDraft = page.data?.status === 'DRAFT';
			(isDraft ? enforceStorageLimit(visitPage) : visitPage)(event, page, ...args);
		},
		[visitPage, enforceStorageLimit],
	);

	const draftText = intl.formatMessage(i18n.untitledDraftText);

	const dragPreview = useCallback((item: any) => <DragPreview item={item} />, []);

	const statusesRef = useRef<GraphQLPageStatus[]>(PAGE_TREE_STATUSES);

	const [scrollToItem] = useScrollToItem({ pageTreeStateUpdatesContainer });

	const [onPageHighlighted] = usePageHighlight({
		pageTreeStateUpdatesContainer,
	});

	const publishPageTreeUpdate = useOutboundPageTreeUpdatePublisher(
		OutboundPageTreeMessage.ContentUpdated,
	);

	const { movePage } = useMovePageHandler();
	const [dndError, setDnDError] = useState<ApolloErrorInterop | null>(null);
	const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
	const [initialPageTreeSize, setInitialPageTreeSize] = useState<number>(0);
	const pageTreeWasSSRRendered = checkSSRPartialSuccess(SSR_PARTIAL_COMPONENT.PageTree);

	if (!renderResourcedEmoji.current && pageTreeWasSSRRendered) {
		renderResourcedEmoji.current = true;
	}

	const managedOnEnterHoverTarget = useCallback(
		(...args: Parameters<typeof onEnterHoverTarget>) => {
			// while a drag is occurring, we don't also items to have their hover action triggered
			if (!getIsDraggingItem()) {
				onEnterHoverTarget(...args);
			}
		},
		[onEnterHoverTarget, getIsDraggingItem],
	);

	const editingTitle = pageTreeStateUpdatesContainer.state.editingTitle;
	const isEditingTitleId = editingTitle?.contentId;
	const setIsEditingTitleId = useCallback(
		(pageId) => {
			return pageTreeStateUpdatesContainer.setIsEditingTitleId(pageId);
		},
		[pageTreeStateUpdatesContainer],
	);

	const clearDelete = useCallback(() => {
		pageTreeStateUpdatesContainer.clearOptimisticDeleteId();
	}, [pageTreeStateUpdatesContainer]);

	/**
	 * We are registering an "open layer" when the quick actions panel is open, or if we are renaming
	 * a page title.
	 *
	 * This is used to keep the side nav flyout open while the user is renaming a page inline.
	 */
	useNotifyOpenLayerObserver({
		isOpen: Boolean(openQuickActionsId || isEditingTitleId),
		// Providing a noop for now. In the future, we may want to cancel any pending renames when a close
		// signal is received. This will be explored in https://jplat.atlassian.net/browse/BLU-7168
		onClose: noop,
	});

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const renderItem = useCallback<PageTreeCoordinatorProps['renderItem']>(
		(params) => {
			const {
				spaceHomePageId,
				updateSinglePage,
				spaceId,
				getContentTreeSize,
				...renderItemParams
			} = params;
			return renderPageTreeItem(renderItemParams, {
				draftText,
				isSuperAdmin,
				spaceHomePageId,
				onClick: handleClick,
				onExpand: renderItemParams.onExpand,
				onCollapse: renderItemParams.onCollapse,
				spaceKey: typeSafeSpaceKey,
				spaceId,
				scrollToItem,
				onPageHighlighted,
				isHoverPageCardOptedIn,
				isQuickActionsEnabled,
				isInlineRenameEnabled,
				closeAllHoverPageCards,
				openQuickActionsId,
				setOpenQuickActionsId,
				focusedQuickActionsId,
				setFocusedQuickActionsId,
				editingTitle,
				setIsEditingTitleId,
				onEnterHoverTarget: managedOnEnterHoverTarget,
				onLeaveHoverTarget,
				onBlurHoverTarget,
				updateSinglePage,
				pageTreeWasSSRRendered,
				renderResourcedEmoji: renderResourcedEmoji.current,
				shouldRenderAfterIcon,
				hideDraftHrefs: shouldBlockCreate,
				getContentTreeSize,
				renderServerPlaceholder,
			});
		},
		[
			draftText,
			isSuperAdmin,
			handleClick,
			scrollToItem,
			typeSafeSpaceKey,
			onPageHighlighted,
			isHoverPageCardOptedIn,
			isQuickActionsEnabled,
			isInlineRenameEnabled,
			closeAllHoverPageCards,
			openQuickActionsId,
			focusedQuickActionsId,
			editingTitle,
			setIsEditingTitleId,
			managedOnEnterHoverTarget,
			onLeaveHoverTarget,
			onBlurHoverTarget,
			pageTreeWasSSRRendered,
			shouldRenderAfterIcon,
			renderResourcedEmoji,
			shouldBlockCreate,
			renderServerPlaceholder,
		],
	);

	useEffect(() => {
		if (pageTreeWasSSRRendered) {
			//set FMP now if SSR rendered and we didn't skip pageTree rendering
			PAGE_TREE_METRIC.markFMP(getMark('CFP-63.ssr-ttr'));
		}
	}, [pageTreeWasSSRRendered]);

	const onLoadError = useCallback(
		(error: Error, options?: { isExpected?: boolean; location?: loadingErrorLocation }) => {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.flagErrorTitle),
				description: location
					? intl.formatMessage(i18n.paginateErrorDescription)
					: intl.formatMessage(i18n.flagErrorDescription),
				isAutoDismiss: true,
			});

			if (options?.isExpected) {
				// This is a temporary measure as we gradually improve PageTree error handling.
				// Normally expected errors should not render a generic error component and
				// instead should provide an error-specific experience.
				experienceTracker.succeed({
					name: PAGE_TREE_EXPERIENCE,
				});
			} else {
				setPageTreeError(error);
				experienceTracker.stopOnError({
					name: PAGE_TREE_EXPERIENCE,
					attributes: { location: options?.location || 'main' },
					error,
				});
			}
		},
		[flags, intl, experienceTracker, setPageTreeError],
	);

	const onPageMove = useCallback(
		(
			moveParams: MovePageParams,
			{
				contentStatus,
				movedPageTitle,
				spaceId,
				source,
				destination,
				instructionType,
				revertMove,
			}: {
				contentStatus: string | null;
				movedPageTitle: string;
				spaceId?: string | null;
				source: TreeSourcePosition;
				destination: TreeDestinationPosition;
				instructionType?: string | null;
				revertMove(): MovePageParams;
			},
		) => {
			createAnalyticsEvent(dragEndUIEvent(true)).fire();

			const handleDragDropSuccess = (
				spaceId,
				pageId,
				sourceParentId,
				destinationParentId,
				instructionType,
				isAmnPage = false,
			) => {
				createAnalyticsEvent(
					dragDropEndTrackEvent({
						source: 'PageTree',
						contentStatus,
						pageId,
						previousParentPageId: sourceParentId,
						newParentPageId: destinationParentId,
						previousSpaceId: spaceId,
						newSpaceId: spaceId,
						isOnEditRoute: isOnEditRouteRef.current,
						instructionType,
						isAmnPage,
					}),
				).fire();
				experienceTracker.succeed({ name: PAGE_TREE_DND_EXPERIENCE });
				publishPageTreeUpdate({ contentId: pageId, parentId: destinationParentId });
				onDragDropSuccess(pageId);
			};

			const handleDndError = (apolloError: ApolloErrorInterop) => {
				// undo the move in state so that the tree reflects that the move did not succeed
				revertMove();

				if (!apolloError) {
					return;
				}

				const i18nMessage = getI18nMessageForKnownError(apolloError);
				if (i18nMessage !== undefined) {
					markErrorAsHandled(apolloError);

					void flags.showErrorFlag({
						title: i18nMessage.title.defaultMessage,
						description: i18nMessage.description.defaultMessage,
						isAutoDismiss: true,
					});

					experienceTracker.succeed({
						name: PAGE_TREE_DND_EXPERIENCE,
						attributes: {
							expectedErrorDescription: i18nMessage.description.defaultMessage,
						},
					});
				} else if (isMovePagePermissionError(apolloError)) {
					markErrorAsHandled(apolloError);

					void flags.showErrorFlag({
						title: intl.formatMessage(i18n.flagMovePagePermissionErrorTitle),
						description: intl.formatMessage(i18n.flagMovePagePermissionErrorDescription),
						isAutoDismiss: true,
					});

					experienceTracker.abort({
						name: PAGE_TREE_DND_EXPERIENCE,
						reason: 'User lacks move permissions',
					});
				} else {
					void flags.showErrorFlag({
						title: intl.formatMessage(i18n.flagErrorTitle),
						description: intl.formatMessage(i18n.flagErrorDescription),
						isAutoDismiss: true,
					});

					experienceTracker.stopOnError({
						name: PAGE_TREE_DND_EXPERIENCE,
						error: apolloError,
					});
				}

				setDnDError(apolloError);
			};

			const makeMovePageRequests = async (
				movePageParams: MovePageParams,
				onMovePageSuccess?: () => void,
			) => {
				// Use allSettled as failures from getAutomatedMeetingNotesPageProperties should not result in a movePage failure
				const [movePageResult, amnPagePropsResult] = await Promise.allSettled([
					movePage(movePageParams).then(onMovePageSuccess),
					getAutomatedMeetingNotesPageProperties(movePageParams.pageId),
				]);

				if (movePageResult.status === 'rejected') {
					throw movePageResult.reason;
				}

				const isAmnPage =
					amnPagePropsResult.status === 'fulfilled' ? amnPagePropsResult.value.isAMN : undefined;
				return {
					movePageResult: movePageResult.value,
					isAmnPage,
				};
			};

			const reverseMove = () => {
				const reverseMoveParams = revertMove();
				makeMovePageRequests(reverseMoveParams)
					.then(({ isAmnPage }) =>
						handleDragDropSuccess(
							spaceId,
							moveParams.pageId,
							// If the move is reversed, the source will be the original destination
							// and the destination will be the original source.
							destination.parentId,
							source.parentId,
							instructionType,
							isAmnPage,
						),
					)
					.catch(handleDndError);
			};

			addUndo({ undo: reverseMove });

			makeMovePageRequests(moveParams, () => showUndoFlag(movedPageTitle))
				.then(({ isAmnPage }) =>
					handleDragDropSuccess(
						spaceId,
						moveParams.pageId,
						source.parentId,
						destination.parentId,
						instructionType,
						isAmnPage,
					),
				)
				.catch(handleDndError);
		},
		[
			experienceTracker,
			flags,
			intl,
			addUndo,
			movePage,
			showUndoFlag,
			createAnalyticsEvent,
			onDragDropSuccess,
			isOnEditRouteRef,
			publishPageTreeUpdate,
		],
	);

	const forceFetchedCallback = useCallback(
		(state) => {
			const lastForceFetchId = pageTreeStateUpdatesContainer.state.forceFetchPageId;
			pageTreeStateUpdatesContainer.clearForceFetch();

			const newContentId = editingTitle?.contentId;

			// For paginated page trees, force fetch id of the newly created content if the content does not
			// have a create content route. This will enable properly scrolling to the new item
			if (
				(state.isTreeTruncatedFollowing || state.isTreeTruncatedPrevious) &&
				editingTitle?.isNewContent &&
				lastForceFetchId !== newContentId
			) {
				pageTreeStateUpdatesContainer.forceFetchWithInlineRename(newContentId, newContentId);
			}
		},
		[pageTreeStateUpdatesContainer, editingTitle],
	);

	const onMoveInvalid = useCallback(
		({
			reason,
		}: {
			reason:
				| 'TREE_TOO_LARGE'
				| 'INVALID_MOVE_LOCATION'
				| 'INVALID_MOVE_LOCATION_DRAFT'
				| 'INVALID_MOVE_LOCATION_MANAGED_FOLDER';
		}) => {
			createAnalyticsEvent(dragEndUIEvent(false)).fire();

			const showLargePageTreeFlag = () => {
				void flags.showWarningFlag({
					title: intl.formatMessage(i18n.flagLargePageTreeTitle),
					description: intl.formatMessage(i18n.flagLargePageTreeDescription),
					actions: [
						{
							content: intl.formatMessage(i18n.flagLargePageTreeSettingsAction),
							onClick: () => {
								push(`/wiki/pages/reorderpages.action?key=${spaceKey}`);
							},
						},
						{
							content: intl.formatMessage(i18n.flagLargePageTreeRefreshAction),
							onClick: () => window.location.reload(),
						},
					],
					isAutoDismiss: true,
				});
			};

			const showInvalidMoveDraftFlag = () => {
				void flags.showErrorFlag({
					title: intl.formatMessage(i18n.flagInvalidMoveDraftTitle),
					description: intl.formatMessage(i18n.flagInvalidMoveDraftDescription),
					isAutoDismiss: true,
				});
			};

			const showInvalidMoveManagedFolderFlag = () => {
				void flags.showErrorFlag({
					title: intl.formatMessage(i18n.flagInvalidMoveManagedFolderTitle),
					description: intl.formatMessage(i18n.flagInvalidMoveManagedFolderDescription),
					isAutoDismiss: true,
				});
			};

			switch (reason) {
				case 'TREE_TOO_LARGE': {
					showLargePageTreeFlag();
					experienceTracker.abort({
						name: PAGE_TREE_DND_EXPERIENCE,
						reason: 'Large page tree limit exceeded',
					});
					break;
				}
				case 'INVALID_MOVE_LOCATION': {
					experienceTracker.abort({
						name: PAGE_TREE_DND_EXPERIENCE,
						reason: 'Invalid move location',
					});
					break;
				}
				case 'INVALID_MOVE_LOCATION_DRAFT': {
					showInvalidMoveDraftFlag();
					experienceTracker.abort({
						name: PAGE_TREE_DND_EXPERIENCE,
						reason: 'Invalid move location onto draft',
					});
					break;
				}
				case 'INVALID_MOVE_LOCATION_MANAGED_FOLDER': {
					showInvalidMoveManagedFolderFlag();
					experienceTracker.abort({
						name: PAGE_TREE_DND_EXPERIENCE,
						reason: 'Invalid move involving managed folder',
					});
					break;
				}
			}
		},
		[createAnalyticsEvent, experienceTracker, flags, intl, push, spaceKey],
	);

	const onDragStart = useCallback(() => {
		closeAllHoverPageCards();
		setOpenQuickActionsId(null);
		createAnalyticsEvent(dragStartUIEvent()).fire();
		experienceTracker.start({
			name: PAGE_TREE_DND_EXPERIENCE,
		});
		hideLastUndoFlag();
	}, [createAnalyticsEvent, experienceTracker, hideLastUndoFlag, closeAllHoverPageCards]);

	const expandAnalyticsCallback = useCallback(
		(page: ContentTreeItem, { spaceId }: { spaceId?: string | null }) => {
			createAnalyticsEvent(
				pageTreeUIAnalytics(
					'expanded',
					spaceId,
					isInDrawer,
					page.data?.status,
					contentIdForAnalyticsRef.current,
					page.data?.type,
					START_TOUCH,
					isOnEditRouteRef.current,
				),
			).fire();
		},
		[createAnalyticsEvent, isInDrawer, isOnEditRouteRef],
	);

	const collapseAnalyticsCallback = useCallback(
		(page: ContentTreeItem, { spaceId }: { spaceId?: string | null }) => {
			createAnalyticsEvent(
				pageTreeUIAnalytics(
					'collapsed',
					spaceId,
					isInDrawer,
					page.data?.status,
					contentIdForAnalyticsRef.current,
					page.data?.type,
					START_TOUCH,
					isOnEditRouteRef.current,
				),
			).fire();
		},
		[createAnalyticsEvent, isInDrawer, isOnEditRouteRef],
	);

	const isDraggable = useCallback(
		(item: any, { space }: any) => {
			if (item.id === isEditingTitleId) {
				return false;
			}
			return canMoveInSpace(space);
		},
		[isEditingTitleId],
	);

	const onLoadMore = useCallback(
		(pagesCount = undefined, spaceId = undefined, direction = undefined) => {
			createAnalyticsEvent(
				pageTreePaginationEvent(spaceId, pagesCount, 'containerNavigation', direction),
			).fire();
		},
		[createAnalyticsEvent],
	);

	/**
	 * onInitialLoadComplete is called after the first initial load of the page tree's data. This includes refreshing the page.
	 * It does not include returning from blog peek, transitioning from blog BACK to page tree, transitioning amongst pages in page tree, etc.
	 */
	const onInitialLoadComplete = useCallback(
		(pagetreeSize?: number) => {
			perfMarkEnd({
				subject: PERFORMANCE_SUBJECT_navigation,
				subjectId: 'NavigationLoading',
				details: {
					navigationView: 'container',
					view: 'space',
					navVersion: 3,
					spaceKey,
				},
			});
			perfMarkEnd({
				subject: PERFORMANCE_SUBJECT_navigationView,
				subjectId: 'SpaceViewLoading',
				details: {
					navigationView: 'container',
					view: 'space',
					navVersion: 3,
					spaceKey,
				},
			});
			experienceTracker.succeed({ name: PAGE_TREE_EXPERIENCE });
			onContentTreeLoadComplete(); // show blog tree spotlight only after page tree finishes loading to prevent misplaced spotlight
			if (pagetreeSize && pagetreeSize > 2) {
				setInitialPageTreeSize(pagetreeSize - 2); //subtract 2 for VIRTUAL_LOCAL_ROOT_ID and VIRTUAL_ROOT_ID
			}
			setInitialLoadComplete(true);
		},
		[experienceTracker, spaceKey, onContentTreeLoadComplete],
	);

	const currentPageId =
		pageTreeStateUpdatesContainer.state.forceFetchPageId ||
		(isPeekingFromBlogs ? homepageId : contentId);

	const isCreateContentRoute = routeName === CREATE_CONTENT.name;

	// Note: content id can be null even when contentIdLoading = false
	// Because a space may not have homepage
	const showPageTree = Boolean(contentId) || !contentIdLoading;
	if (!showPageTree) {
		return null;
	}

	return (
		<Fragment>
			<PageSegmentLoadStart metric={PAGE_TREE_METRIC} />
			<ExperienceStart key={`experience-${spaceKey}`} name={PAGE_TREE_EXPERIENCE} id={spaceKey} />
			{dndError && <ErrorDisplay error={dndError} />}
			<GeneralShortcutListener accelerator={UNDO_SHORTCUT} listener={onUndoShortcut} />
			<PageTreeCoordinator
				// CONFCLOUD-69158 The key here is forcing page tree to re-mount when switching from space to space
				// This preventing issue when transition from a space that has homepage to a space without
				key={spaceKey}
				statuses={statusesRef.current}
				isPeekingFromBlogs={isPeekingFromBlogs}
				onPageMove={onPageMove}
				onDragStart={onDragStart}
				onMoveInvalid={onMoveInvalid}
				onLoadError={onLoadError}
				onExpand={expandAnalyticsCallback}
				onCollapse={collapseAnalyticsCallback}
				onLoadMore={onLoadMore}
				onInitialLoadComplete={onInitialLoadComplete}
				currentPageId={currentPageId}
				highlightPages={pageTreeStateUpdatesContainer.state.highlightPagesIds}
				forceFetchCurrentPage={Boolean(pageTreeStateUpdatesContainer.state.forceFetchPageId)}
				forceFetchedCallback={forceFetchedCallback}
				optimisticDeleteId={pageTreeStateUpdatesContainer.state.optimisticDeletePageId || undefined}
				clearDeleteId={clearDelete}
				spaceKey={typeSafeSpaceKey}
				renderItem={renderItem}
				dragPreview={dragPreview}
				isDraggable={isDraggable}
				setIsPageTreeLoading={setIsPageTreeLoading}
				isSuperAdmin={isSuperAdmin}
				getIsDraggingItem={getIsDraggingItem}
				setIsDraggingItem={setIsDraggingItem}
				setOpenQuickActionsId={setOpenQuickActionsId}
				renderResourcedEmoji={renderResourcedEmoji.current}
				setContentTreeSize={setContentTreeSize}
				pageTreeStateUpdatesContainer={pageTreeStateUpdatesContainer}
				isCreateContentRoute={isCreateContentRoute}
				flags={flags}
			/>
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{(initialLoadComplete || process.env.REACT_SSR) && (
				<Fragment>
					<PageSegmentLoadEnd
						metric={PAGE_TREE_METRIC}
						customData={{
							initialPageTreeSize,
							skipTreeRenderSSR: !pageTreeWasSSRRendered, //tracks if Best effort failed & if tree was over 200 pages
						}}
						stopTime={pageTreeWasSSRRendered ? getMark('CFP-63.ssr-ttr') : undefined}
					/>
				</Fragment>
			)}
		</Fragment>
	);
};
export const PageTree: FC<PageTreeProps> = memo((props) => {
	return (
		<ErrorBoundary attribution={Attribution.DISCO}>
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					<Subscribe to={[PageTreeStateUpdater]}>
						{(pageTreeStateUpdatesContainer: PageTreeStateUpdater) => {
							return (
								<PageTreeInner
									{...props}
									pageTreeStateUpdatesContainer={pageTreeStateUpdatesContainer}
									flags={flags}
								/>
							);
						}}
					</Subscribe>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
});

PageTree.displayName = 'PageTree';
